import {
  FileUp,
  MapPin,
  MessageSquare,
  Phone,
  Plus,
  Search,
} from "lucide-react";
import { ListBase, useListContext, useTranslate, type RaRecord } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";

import { DataTable } from "@/components/admin/data-table";
import { List } from "@/components/admin/list";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import type { Sale } from "@/components/atomic-crm/types";

import { AccionesEnBloque } from "./AccionesEnBloque";
import { ProximoIntento } from "./ProximoIntento";
import { EtiquetaWeb } from "./EtiquetaWeb";
import { useAjustes } from "../datos/useAjustes";
import { CampanaAvisos } from "../equipo/CampanaAvisos";
import { useEsAdmin } from "../datos/useEsAdmin";
import { FiltroPersonasSelect } from "../equipo/FiltroPersonasSelect";
import { useFiltroPersonas } from "../equipo/useFiltroPersonas";
import { textoDatosQueFaltan } from "../dominio/completitud";
import { enlaceLlamar } from "../dominio/contacto";
import { claveOrigen, ORIGENES } from "../dominio/catalogos";
import { grupoLlamada, ORDEN_LLAMADA } from "../dominio/llamadas";
import { FILTRO_LEADS } from "../dominio/panel";
import type { Canal, Negocio } from "../dominio/tipos";
import { MarcaCabecera } from "../layout/MarcaCabecera";
import { formatoFechaHora } from "./formato";
import { ResultadoSheet } from "./ResultadoSheet";
import { Semaforo } from "./Semaforo";
import { useAbrirFicha } from "./useAbrirFicha";
import { BotonBuscar } from "./BuscadorFichas";

// Leads = solo los pendientes de llamar (Robert, 02/10/2026): proyecto de Ventas en «Lead
// nuevo» (nuevos y reintentos de «No contesta») sin «no volver a contactar»; el mismo
// filtro que cuenta el Inicio (dominio/panel.ts). Tras el primer contacto la ficha vive en
// el Pipeline; cualquier ficha se encuentra con «Buscar». Una sola forma de hacer cada cosa.

type HojaAbierta = { negocio: Negocio; canal: Canal } | null;

/** Leads (SPEC.md §2): pendientes de llamar. La ficha se abre en la ventana. */
export const NegocioList = () => {
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const abrirFicha = useAbrirFicha();
  const [hoja, setHoja] = useState<HojaAbierta>(null);
  const filtros = useFiltros();
  const esAdmin = useEsAdmin();
  // Robert, 13/10/2026: Jan y Robert filtran Leads por persona (selección múltiple).
  // Robert, 15/10/2026: sin el botón «Ver los descubiertos por Prospect» (S10b): los
  // descubiertos no salen en Leads.
  const [personas, setPersonas] = useFiltroPersonas();
  const filtro = {
    ...FILTRO_LEADS,
    ...(esAdmin && personas.length ? { personas } : {}),
  };
  const alResultado = setHoja;

  // v1.1: tras registrar un resultado desde la lista, aviso con «Completar ficha» (y qué
  // datos faltan) para ir a la ficha si se quiere; nunca obliga.
  const avisarCompletar = (negocio: Negocio) => {
    toast(negocio.nombre, {
      description: textoDatosQueFaltan(translate, negocio) ?? undefined,
      action: {
        label: translate("nevora.negocios.completar_ficha"),
        onClick: () => abrirFicha(negocio.negocio_id),
      },
    });
  };

  const cabecera = (
    <div className="mb-3 md:mb-0 space-y-2">
      {!isMobile ? (
        <h1 className="titulo-pagina">{translate("nevora.negocios.leads")}</h1>
      ) : null}
      <p className="text-sm text-muted-foreground">
        {translate("nevora.negocios.leads_ayuda")}
      </p>
      {esAdmin ? (
        <div className="flex flex-wrap items-center gap-2">
          <FiltroPersonasSelect valor={personas} cambiar={setPersonas} />
        </div>
      ) : null}
    </div>
  );
  const hojaResultado = hoja ? (
    <ResultadoSheet
      negocio={hoja.negocio}
      canal={hoja.canal}
      open
      onOpenChange={(abierta) => !abierta && setHoja(null)}
      onGuardado={avisarCompletar}
    />
  ) : null;

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MarcaCabecera />
          <div className="ml-auto flex items-center gap-1">
            <BotonBuscar />
            <CampanaAvisos plegada alinear="end" />
          </div>
        </MobileHeader>
        <MobileContent>
          {cabecera}
          <div data-guia="leads-lista">
            <ListBase<Negocio>
              resource="negocios"
              filter={filtro}
              sort={{ field: ORDEN_LLAMADA, order: "ASC" }}
              perPage={50}
              storeKey="negocios.movil.leads.46"
              disableSyncWithLocation
            >
              <ListaMovil onResultado={alResultado} />
            </ListBase>
          </div>
          {hojaResultado}
        </MobileContent>
      </>
    );
  }

  return (
    <div className="mt-2 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        {cabecera}
        <div className="flex flex-wrap items-center gap-2">
          {esAdmin ? (
            // Auditoría 30/10/2026: «Importar CSV», aquí en vez de en el menú.
            <Button asChild variant="outline">
              <Link to="/importar">
                <FileUp className="size-4 mr-1" />
                {translate("nevora.nav.importar")}
              </Link>
            </Button>
          ) : null}
          <Button asChild>
            <Link to="/negocios/create">
              <Plus className="size-4 mr-1" />
              <TextoNuevaFicha />
            </Link>
          </Button>
        </div>
      </div>
      <div data-guia="leads-lista">
        <List<Negocio>
          resource="negocios"
          filter={filtro}
          filters={filtros}
          sort={{ field: ORDEN_LLAMADA, order: "ASC" }}
          perPage={25}
          storeKey="negocios.leads.46"
          // Filtros solo en el store, no en la URL: la sincronización con la URL de ra-core
          // va con debounce y, si se abre una ficha justo después de buscar, devolvía a la
          // lista (visto en las pruebas e2e A4/A17 el 25/09/2026).
          disableSyncWithLocation
          title={false}
          actions={false}
          disableBreadcrumb
        >
          <TablaNegocios onResultado={alResultado} />
        </List>
      </div>
      {hojaResultado}
    </div>
  );
};

/** Prioridad que da Nevora Prospect al entregar el lead (S9): 1 = llamar primero. */
const Prioridad = ({ negocio }: { negocio: Negocio }) => {
  const translate = useTranslate();
  const { ajustes } = useAjustes();
  if (!negocio.prospect_prioridad && !negocio.prospect_frio_at) return null;
  return (
    <>
      {negocio.prospect_prioridad ? (
        <Badge
          variant="outline"
          className="shrink-0"
          title={translate("nevora.para_llamar.prioridad_ayuda")}
        >
          {translate("nevora.para_llamar.prioridad", {
            n: negocio.prospect_prioridad,
          })}
        </Badge>
      ) : null}
      {negocio.prospect_frio_at ? (
        <Badge
          variant="outline"
          className="shrink-0 border-amber-600 text-amber-800 dark:text-amber-300"
          title={translate("nevora.para_llamar.frio_ayuda", {
            dias: ajustes.diasFrio,
          })}
        >
          {translate("nevora.para_llamar.frio")}
        </Badge>
      ) : null}
    </>
  );
};

const TextoNuevaFicha = () => {
  const translate = useTranslate();
  return <>{translate("nevora.negocios.nueva_ficha")}</>;
};

/** Filtros de escritorio: búsqueda, sector, origen, asignado. */
const useFiltros = (): React.ReactNode[] => {
  const translate = useTranslate();
  const { ajustes } = useAjustes();
  // Un setter solo ve sus fichas: sin filtro por persona (Robert, 09/10/2026).
  const esAdmin = useEsAdmin();
  return [
    <SearchInput
      key="q"
      source="q"
      alwaysOn
      placeholder={translate("nevora.negocios.buscar")}
    />,
    <SelectInput
      key="sector"
      source="sector"
      label="nevora.negocios.filtros.sector"
      choices={ajustes.sectores.map((s) => ({ id: s, name: s }))}
      helperText={false}
    />,
    <SelectInput
      key="origen"
      source="origen"
      label="nevora.negocios.filtros.origen"
      choices={ORIGENES.map((o) => ({
        id: o,
        name: translate(claveOrigen(o)),
      }))}
      helperText={false}
    />,
    esAdmin ? (
      <ReferenceInput
        key="asignado_a"
        source="asignado_a"
        reference="sales"
        filter={{ disabled: false }}
      >
        <SelectInput
          label="nevora.negocios.filtros.asignado"
          optionText={(r: Sale) => `${r.first_name} ${r.last_name}`}
          helperText={false}
        />
      </ReferenceInput>
    ) : null,
  ].filter(Boolean);
};

const BotonesResultado = ({
  negocio,
  onResultado,
  grande = false,
}: {
  negocio: Negocio;
  onResultado: ((h: HojaAbierta) => void) | null;
  grande?: boolean;
}) => {
  const translate = useTranslate();
  if (negocio.no_llamar || !onResultado) return null;
  return (
    <div className={cn("flex gap-2", grande && "flex-1 min-w-0")}>
      <Button
        size={grande ? "default" : "sm"}
        className={cn(grande && "flex-1 h-11 px-2")}
        onClick={(e) => {
          e.stopPropagation();
          onResultado({ negocio, canal: "llamada" });
        }}
      >
        <Phone className="size-4 mr-1" />
        {translate("nevora.negocios.he_llamado")}
      </Button>
      {/* Auditoría 30/10/2026: «He visitado», solo con el icono (los setters no visitan). */}
      <Button
        size="icon"
        variant="outline"
        className={cn("shrink-0", grande ? "h-11 w-11" : "h-8 w-8")}
        aria-label={translate("nevora.negocios.he_visitado")}
        title={translate("nevora.negocios.he_visitado")}
        onClick={(e) => {
          e.stopPropagation();
          onResultado({ negocio, canal: "visita" });
        }}
      >
        <MapPin className="size-4" />
      </Button>
      <Button
        size="icon"
        variant="outline"
        className={cn("shrink-0", grande ? "h-11 w-11" : "h-8 w-8")}
        aria-label={translate("nevora.negocios.he_escrito")}
        title={translate("nevora.negocios.he_escrito")}
        onClick={(e) => {
          e.stopPropagation();
          onResultado({ negocio, canal: "whatsapp" });
        }}
      >
        <MessageSquare className="size-4" />
      </Button>
    </div>
  );
};

const TablaNegocios = ({
  onResultado,
}: {
  onResultado: ((h: HojaAbierta) => void) | null;
}) => {
  const translate = useTranslate();
  const abrirFicha = useAbrirFicha();
  const esAdmin = useEsAdmin();
  const { data, isPending, filterValues } = useListContext<Negocio>();
  const ahora = new Date();
  if (!isPending && data && data.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center">
        {translate(
          Object.keys(filterValues ?? {}).length
            ? "nevora.negocios.sin_resultados"
            : "nevora.negocios.sin_leads",
        )}
      </p>
    );
  }
  return (
    <DataTable<Negocio>
      rowClick={(id) => {
        abrirFicha(id);
        return false;
      }}
      // Auditoría 30/10/2026: Jan y Robert marcan varias y las asignan o las devuelven.
      bulkActionButtons={esAdmin ? <AccionesEnBloque /> : false}
      // 46: los reintentos que aún no tocan, atenuados.
      rowClassName={(r) =>
        grupoLlamada(r, ahora) === 4 ? "opacity-60" : undefined
      }
    >
      <DataTable.Col
        label={false}
        render={(r: Negocio) => <Semaforo negocio={r} />}
      />
      <DataTable.Col
        source="nombre"
        render={(r: Negocio) => (
          // Rediseño 30/10/2026: persona y sector debajo del nombre (la tabla cabe sin desplazarse).
          <div className="min-w-0 space-y-0.5">
            <span className="flex flex-wrap items-center gap-2 font-medium">
              {r.nombre}
              {r.origen === "web" ? (
                <EtiquetaWeb fuente={r.fuente_web} />
              ) : null}
              <Prioridad negocio={r} />
            </span>
            {r.persona_contacto || r.sector ? (
              <span className="block text-xs text-muted-foreground">
                {[r.persona_contacto, r.sector].filter(Boolean).join(" · ")}
              </span>
            ) : null}
          </div>
        )}
      />
      <DataTable.Col source="telefono" />
      {/* Auditoría 30/10/2026: el setter no ve «Asignado a» (siempre es él). */}
      {esAdmin ? (
        <DataTable.Col
          source="asignado_a"
          render={(r: Negocio) =>
            r.asignado_a ? (
              <ReferenceField
                record={r as RaRecord}
                source="asignado_a"
                reference="sales"
                link={false}
              />
            ) : (
              <span className="text-muted-foreground">
                {translate("nevora.negocios.sin_asignar")}
              </span>
            )
          }
        />
      ) : null}
      <DataTable.Col
        source="ultimo_contacto"
        render={(r: Negocio) =>
          r.ultimo_contacto
            ? formatoFechaHora(r.ultimo_contacto)
            : translate("nevora.negocios.nunca")
        }
      />
      <DataTable.Col source="intentos" />
      <DataTable.Col
        label={translate("nevora.llamadas.proximo_intento")}
        render={(r: Negocio) => <ProximoIntento negocio={r} ahora={ahora} />}
      />
      <DataTable.Col
        label={false}
        render={(r: Negocio) => (
          <BotonesResultado negocio={r} onResultado={onResultado} />
        )}
      />
    </DataTable>
  );
};

/** Libreta de campo (SPEC.md §7): tarjetas grandes, llamar en un toque, resultado en tres. */
const ListaMovil = ({
  onResultado,
}: {
  onResultado: ((h: HojaAbierta) => void) | null;
}) => {
  const translate = useTranslate();
  const {
    data,
    isPending,
    filterValues,
    setFilters,
    perPage,
    setPerPage,
    total,
  } = useListContext<Negocio>();
  const [busqueda, setBusqueda] = useState<string>(
    (filterValues?.q as string) ?? "",
  );

  return (
    <div className="space-y-3 pb-6">
      <div className="relative">
        <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          className="pl-9"
          placeholder={translate("nevora.negocios.buscar")}
          value={busqueda}
          onChange={(e) => {
            setBusqueda(e.target.value);
            setFilters({ ...filterValues, q: e.target.value || undefined });
          }}
        />
      </div>
      {!isPending && data && data.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center">
          {translate(
            busqueda
              ? "nevora.negocios.sin_resultados"
              : "nevora.negocios.sin_leads",
          )}
        </p>
      ) : null}
      <ul className="space-y-3">
        {(data ?? []).map((n) => (
          <TarjetaMovil key={n.id} negocio={n} onResultado={onResultado} />
        ))}
      </ul>
      {total !== undefined && data && total > data.length ? (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setPerPage(perPage + 50)}
        >
          {translate("crm.common.load_more")}
        </Button>
      ) : null}
    </div>
  );
};

const TarjetaMovil = ({
  negocio,
  onResultado,
}: {
  negocio: Negocio;
  onResultado: ((h: HojaAbierta) => void) | null;
}) => {
  const translate = useTranslate();
  const abrirFicha = useAbrirFicha();
  const tel = enlaceLlamar(negocio.telefono);
  return (
    <li className="rounded-lg border bg-card p-4 space-y-3">
      <button
        type="button"
        onClick={() => abrirFicha(negocio.negocio_id)}
        className="block w-full space-y-1 text-left"
      >
        <div className="flex items-center gap-2">
          <Semaforo negocio={negocio} />
          <span className="font-medium truncate flex-1">{negocio.nombre}</span>
          {negocio.origen === "web" ? (
            <EtiquetaWeb fuente={negocio.fuente_web} />
          ) : null}
          <Prioridad negocio={negocio} />
        </div>
        <p className="text-sm text-muted-foreground truncate">
          {[negocio.persona_contacto, negocio.telefono, negocio.direccion]
            .filter(Boolean)
            .join(" · ") || translate("nevora.negocios.nunca")}
        </p>
        <div className="flex items-center justify-between gap-2">
          {negocio.intentos > 0 ? (
            <p className="text-xs text-muted-foreground">
              {translate("nevora.negocios.intentos", {
                smart_count: negocio.intentos,
              })}
            </p>
          ) : (
            <span />
          )}
          {/* 46: cuándo toca llamar. */}
          <ProximoIntento negocio={negocio} />
        </div>
      </button>
      <div className="flex gap-2 items-stretch">
        {tel ? (
          <Button
            asChild
            variant="outline"
            size="icon"
            className="h-11 w-11 shrink-0"
          >
            <a href={tel} aria-label={translate("nevora.negocios.llamar")}>
              <Phone className="size-5" />
            </a>
          </Button>
        ) : null}
        <BotonesResultado negocio={negocio} onResultado={onResultado} grande />
      </div>
    </li>
  );
};
